import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  Stamp,
  Home,
  ArrowRight,
  Clock,
  DollarSign,
} from "lucide-react";
import LandingLayout from "layouts/LandingLayout";
import useInView from "helpers/useInView";
import Hero, {
  SlantedBackground,
  StyledResponsiveVideoEmbed,
} from "components/hero/BackgroundAsImage";
import CtaFinal from "components/cta/CtaFinal";
import MainFeature2 from "components/features/TwoColSingleFeatureWithStats2";
import Testimonial from "components/testimonials/TwoColumnWithImageAndProfilePictureReview";
import { DESTINATIONS, STATS } from "constants/services";
import {
  fadeUpVariants,
  staggerContainerVariants,
  staggerItemVariants,
} from "lib/animations";
import { IMAGES } from "constants/images";
import AppButton from "components/misc/Buttons";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-9.svg";

// ─── Animation ───────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

/** Wrapper qui anime ses enfants à l'entrée dans le viewport */
const FadeUpSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
  style?: React.CSSProperties;
}> = ({ children, delay = 0, style }) => {
  const [ref, inView] = useInView({ once: true });
  return (
    <MDiv
      ref={ref as React.Ref<HTMLDivElement>}
      variants={fadeUpVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
      style={style}
    >
      {children}
    </MDiv>
  );
};

// ─── Styled ──────────────────────────────────────────────────────────────────
const HighlightedText = tw.span`bg-primary-500 text-gray-100 px-4 transform -skew-x-12 inline-block`;
const Subheading = tw.span`tracking-wider text-sm font-medium text-primary-500`;
const Description = tw.span`inline-block mt-8`;

// Stats bar
const StatsBar = styled.div`
  ${tw`bg-white border-b border-primary-200`}
  box-shadow: 0 1px 0 rgba(0,0,0,0.06);
`;
const StatsBarInner = tw.div`max-w-screen-xl mx-auto px-8 py-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100 text-center`;
const StatItem = tw.div`px-4`;
const StatVal = styled.p`
  ${tw`text-3xl font-black`}
  color: #ff6c00;
`;
const StatLabel = tw.p`text-xs text-gray-500 font-semibold uppercase tracking-widest mt-1`;

// Services grid
const SectionWrapper = tw.div`max-w-screen-xl mx-auto px-8 py-20`;
const SectionTag = styled.p`
  ${tw`text-xs font-bold uppercase tracking-widest text-center mb-3`}
  color: #ff6c00;
`;
const SectionTitle = tw.h2`text-3xl md:text-4xl font-black text-gray-900 text-center mb-3`;
const SectionSub = tw.p`text-gray-500 text-center max-w-2xl mx-auto mb-12 leading-relaxed`;
const ServicesGrid = tw.div`grid grid-cols-1 lg:grid-cols-3 border border-gray-200`;
const ServiceCard = styled(Link)`
  ${tw`bg-white p-10 no-underline transition-all duration-300 border-r border-gray-200`}
  &:last-child {
    border-right: none;
  }
  &:hover {
    background: #fff8f4;
  }
  &:hover .service-icon {
    color: #ff6c00;
  }
  &:hover .service-title {
    color: #ff6c00;
  }
  &:hover .service-arrow {
    transform: translateX(6px);
    color: #ff6c00;
  }
`;
const ServiceIconWrap = styled.div`
  ${tw`mb-6`}
  .service-icon {
    color: #111111;
    transition: color 0.3s ease;
  }
`;
const ServiceTitle = tw.h3`text-xl font-black text-gray-900 mb-3 transition-colors`;
const ServiceDesc = tw.p` text-gray-500 leading-relaxed mb-6`;
const ServiceFeatures = tw.ul`space-y-2 mb-8`;
const ServiceFeatureItem = tw.li`flex items-center gap-2 text-sm text-gray-500`;
const ServiceArrow = styled.div`
  ${tw`text-sm font-bold flex items-center gap-2 transition-all duration-300`}
  color: #111111;
`;

// Destinations
const DestinationsGrid = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200`;
const DestCard = styled(Link)`
  ${tw`relative overflow-hidden block no-underline transition-all duration-300`}
  height: 22rem;
`;
const DestBg = styled.div<{ src: string }>`
  ${tw`absolute inset-0 bg-cover bg-center transition-transform duration-700`}
  background-image: url(${(p) => p.src});
  ${DestCard}:hover & {
    transform: scale(1.06);
  }
`;
const DestOverlay = styled.div`
  ${tw`absolute inset-0`}
  background: linear-gradient(to top, rgba(17,17,17,0.95) 0%, rgba(17,17,17,0.4) 55%, transparent 100%);
`;
const DestContent = tw.div`absolute bottom-0 left-0 right-0 p-6`;
const DestFlag = tw.div`text-3xl mb-3`;
const DestName = tw.h3`text-xl font-black text-white mb-1 uppercase tracking-wide`;
const DestTagline = tw.p`text-gray-400 text-xs leading-relaxed`;
const DestMeta = tw.div`flex items-center gap-3 mt-4`;
const DestMetaBadge = styled.div`
  ${tw`text-white text-xs px-3 py-1 font-semibold uppercase tracking-wider`}
  background: rgba(255,108,0,0.7);
  border-left: 2px solid #ff6c00;
`;

// How it works
const StepsSection = styled.div`
  background: #111111;
  ${tw`py-24`}
  margin-left: -2rem;
  margin-right: -2rem;
`;
const StepsWrapper = tw.div`max-w-screen-xl mx-auto px-16`;
const StepsGrid = tw.div`grid grid-cols-1 md:grid-cols-4 gap-0 mt-12 border-t border-gray-700`;
const StepItem = styled.div`
  ${tw`pt-10 pr-8`}
  border-right: 1px solid #222;
  &:last-child {
    border-right: none;
  }
`;
const StepNumber = styled.div`
  font-size: 3.5rem;
  font-weight: 900;
  line-height: 1;
  color: #ff6c00;
  margin-bottom: 1rem;
`;
const StepTitle = tw.h4`font-bold text-white mb-2`;
const StepDesc = tw.p`text-sm text-gray-500 leading-relaxed`;

// Secondary banner (fond noir)
const SecondaryBanner = styled.div`
  background: #111111;
  ${tw`py-16`}
  margin-left: -2rem;
  margin-right: -2rem;
`;
const SecondaryBannerInner = tw.div`max-w-screen-xl mx-auto px-16 py-8 grid grid-cols-1 md:grid-cols-3 gap-px`;
const SecondaryBannerItem = styled.div`
  ${tw`flex items-start gap-5 px-10 py-8`}
  border-right: 1px solid #222;
  &:last-child {
    border-right: none;
  }
`;
const SecondaryBannerIcon = styled.div`
  ${tw`flex-shrink-0 w-12 h-12 flex items-center justify-center`}
  color: #ff6c00;
`;
const SecondaryBannerItemTitle = tw.h4`text-white text-xl font-black mb-1`;
const SecondaryBannerItemDesc = tw.p`text-gray-500 leading-relaxed`;

// Trust banner
const TrustBanner = styled.div`
  background: #ff6c00;
  ${tw`py-10`}
  margin-left: -2rem;
  margin-right: -2rem;
`;
const TrustBannerInner = tw.div`max-w-screen-xl mx-auto px-16 py-8 flex flex-col md:flex-row items-center justify-between gap-10`;
const TrustBannerText = tw.div`text-center md:text-left`;
const TrustBannerTitle = tw.h3`text-2xl md:text-3xl my-4 font-black text-white leading-tight`;
const TrustBannerSub = tw.p`text-white mt-4 font-medium`;
const TrustBannerCTA = styled(Link)`
  ${tw`flex-shrink-0 px-8 py-4 bg-white font-black no-underline transition-all duration-300 whitespace-nowrap inline-flex items-center gap-2`}
  color: #ff6c00;
  &:hover {
    background: #111111;
    color: #ffffff;
  }
`;

const DecoratorBlobContainer = tw.div`absolute inset-0 overflow-hidden rounded-lg`;
const DecoratorBlob1 = tw(
  SvgDecoratorBlob1,
)`absolute top-0 left-0 w-80 h-80 transform -translate-x-20 -translate-y-32 text-primary-700 opacity-15`;
const DecoratorBlob2 = tw(
  SvgDecoratorBlob1,
)`absolute bottom-0 right-0 w-80 h-80 transform  translate-x-32 translate-y-48 text-primary-700 opacity-25`;

// ─── Component ────────────────────────────────────────────────────────────────
const HomePage: React.FC = () => {
  return (
    <LandingLayout>
      {/* ──────── NAVBAR + HERO ──────── */}
      <Hero
        backgroundImageSrc="https://images.unsplash.com/photo-1522071901873-411886a10004?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80"
        overlayOpacity={0.55}
        notification="MOMS EDUC STUDY — Le garant financier de vos études."
        heading={
          <>
            <span>Votre avenir académique</span>
            <br />
            <SlantedBackground>commence ici.</SlantedBackground>
          </>
        }
        description="Attestation AVI, visa, logement, billets. Nous accompagnons chaque étudiant africain de A à Z pour réussir sa mobilité internationale."
        primaryButton={{ text: "Commencer ma démarche", href: "/contact" }}
        secondaryButton={{ text: "Voir les services", href: "/services" }}
        rightContent={
          <StyledResponsiveVideoEmbed url="" background="transparent" />
        }
      />

      {/* ──────── STATS BAR ──────── */}
      <FadeUpSection>
        <StatsBar>
          <StatsBarInner>
            <MDiv
              style={{ display: "contents" }}
              variants={staggerContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {STATS.map((s) => (
                <MDiv
                  key={s.label}
                  variants={staggerItemVariants}
                  style={{ display: "contents" }}
                >
                  <StatItem>
                    <StatVal>{s.value}</StatVal>
                    <StatLabel>{s.label}</StatLabel>
                  </StatItem>
                </MDiv>
              ))}
            </MDiv>
          </StatsBarInner>
        </StatsBar>
      </FadeUpSection>

      {/* ──────── SERVICES ──────── */}
      <SectionWrapper>
        <FadeUpSection>
          <SectionTag>Nos Services</SectionTag>
          <SectionTitle>
            Tout ce dont vous avez besoin,{" "}
            <HighlightedText>en un seul endroit.</HighlightedText>
          </SectionTitle>
          <SectionSub>
            De l'attestation AVI jusqu'à votre installation, nous couvrons
            chaque étape de votre projet d'études à l'étranger.
          </SectionSub>
        </FadeUpSection>
        <ServicesGrid>
          <MDiv
            style={{ display: "contents" }}
            variants={staggerContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              {
                slug: "avi",
                route: "/services/avi",
                icon: <FileText size={40} className="service-icon" />,
                title: "Attestation AVI",
                desc: "Obtenez votre Attestation de Virement Irrévocable certifiée en 48h, acceptée par tous les consulats et Campus France.",
                features: [
                  "Traitement prioritaire en 48h",
                  "Document 100% certifié consulat",
                  "Vérification en ligne par QR code",
                ],
              },
              {
                slug: "visa",
                route: "/services/visa",
                icon: <Stamp size={40} className="service-icon" />,
                title: "Accompagnement Visa",
                desc: "Constitution de dossier, simulation d'entretien consulaire et suivi en temps réel jusqu'à l'obtention de votre visa.",
                features: [
                  "Analyse complète du dossier",
                  "Simulation entretien consulaire",
                  "Relances et suivi en temps réel",
                ],
              },
              {
                slug: "logement",
                route: "/services/logement",
                icon: <Home size={40} className="service-icon" />,
                title: "Logement Étudiant",
                desc: "Résidence garantie et réservée avant votre départ, sécurisée et proche de votre université.",
                features: [
                  "Réservation avant votre départ",
                  "Logements proches universités",
                  "Contrat de bail vérifié",
                ],
              },
            ].map((service) => (
              <MDiv
                key={service.slug}
                variants={staggerItemVariants}
                style={{ display: "contents" }}
              >
                <ServiceCard to={service.route}>
                  <ServiceIconWrap>{service.icon}</ServiceIconWrap>
                  <ServiceTitle className="service-title">
                    {service.title}
                  </ServiceTitle>
                  <ServiceDesc>{service.desc}</ServiceDesc>
                  <ServiceFeatures>
                    {service.features.map((f) => (
                      <ServiceFeatureItem key={f}>
                        <span style={{ color: "#ff6c00", fontWeight: 700 }}>
                          —
                        </span>{" "}
                        {f}
                      </ServiceFeatureItem>
                    ))}
                  </ServiceFeatures>
                  <ServiceArrow className="service-arrow">
                    En savoir plus <ArrowRight size={16} />
                  </ServiceArrow>
                </ServiceCard>
              </MDiv>
            ))}
          </MDiv>
        </ServicesGrid>

        <FadeUpSection delay={0.2}>
          <div tw="flex justify-center mt-12">
            <AppButton
              variant="primary"
              size="lg"
              to="/services"
              iconRight={<ArrowRight size={16} />}
              style={{ marginTop: "1.5rem" }}
            >
              Consulter tous nos services
            </AppButton>
          </div>
        </FadeUpSection>
      </SectionWrapper>

      {/* ──────── SECONDARY BANNER ──────── */}
      <SecondaryBanner>
        <SecondaryBannerInner>
          <MDiv
            style={{ display: "contents" }}
            variants={staggerContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              {
                icon: <FileText size={36} />,
                title: "AVI certifiée en 48h",
                desc: "Document officiel accepté par tous les consulats et ambassades pour votre dossier de visa.",
              },
              {
                icon: <Stamp size={36} />,
                title: "Taux de visa à 94%",
                desc: "Notre accompagnement personnalisé maximise vos chances d'obtention du visa étudiant.",
              },
              {
                icon: <Home size={36} />,
                title: "Logement garanti",
                desc: "Votre hébergement réservé et sécurisé avant même votre départ du pays.",
              },
            ].map((item) => (
              <MDiv
                key={item.title}
                variants={staggerItemVariants}
                style={{ display: "contents" }}
              >
                <SecondaryBannerItem>
                  <SecondaryBannerIcon>{item.icon}</SecondaryBannerIcon>
                  <div>
                    <SecondaryBannerItemTitle>
                      {item.title}
                    </SecondaryBannerItemTitle>
                    <SecondaryBannerItemDesc>
                      {item.desc}
                    </SecondaryBannerItemDesc>
                  </div>
                </SecondaryBannerItem>
              </MDiv>
            ))}
          </MDiv>
        </SecondaryBannerInner>
      </SecondaryBanner>

      {/* ──────── À PROPOS ──────── */}
      <div tw="relative">
        <MainFeature2
          subheading={<Subheading>Qui sommes-nous ?</Subheading>}
          heading={
            <>
              Votre partenaire de confiance <wbr />
              <HighlightedText>depuis 2019.</HighlightedText>
            </>
          }
          description={
            <Description>
              MomsEduc Study est né de la conviction que chaque étudiant
              africain mérite un accompagnement professionnel et humain pour
              réaliser son projet d'études à l'étranger.
              <br />
              <br />
              Notre équipe pluridisciplinaire (juristes, conseillers, anciens
              étudiants expatriés) vous guide à chaque étape, de la constitution
              de votre dossier jusqu'à votre installation.
            </Description>
          }
          primaryButtonText="Notre histoire"
          primaryButtonUrl="/about"
          imageSrc={IMAGES.confianceImg}
          imageCss={tw`shadow-xl`}
          imageDecoratorBlob={false}
          textOnLeft={false}
          statistics={[
            { key: "Clients", value: "228+" },
            { key: "Projects", value: "10+" },
          ]}
        />
        <DecoratorBlobContainer>
          <DecoratorBlob1 />
          <DecoratorBlob2 />
        </DecoratorBlobContainer>
      </div>

      {/* ──────── DESTINATIONS ──────── */}
      <SectionWrapper>
        <FadeUpSection>
          <SectionTag>Destinations</SectionTag>
          <SectionTitle>
            Choisissez votre <HighlightedText>destination.</HighlightedText>
          </SectionTitle>
          <SectionSub>
            France, Belgique, Canada, Luxembourg — nous maîtrisons les
            procédures de chaque pays.
          </SectionSub>
        </FadeUpSection>
        <DestinationsGrid>
          <MDiv
            style={{ display: "contents" }}
            variants={staggerContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {DESTINATIONS.map((dest) => (
              <MDiv
                key={dest.code}
                variants={staggerItemVariants}
                style={{ display: "contents" }}
              >
                <DestCard to={dest.route}>
                  <DestBg src={dest.imageSrc} />
                  <DestOverlay />
                  <DestContent>
                    <DestFlag>{dest.flag}</DestFlag>
                    <DestName>{dest.name}</DestName>
                    <DestTagline>{dest.tagline}</DestTagline>
                    <DestMeta>
                      <DestMetaBadge>
                        <Clock
                          size={11}
                          style={{
                            display: "inline",
                            marginRight: 4,
                            verticalAlign: "middle",
                          }}
                        />
                        {dest.processingTime}
                      </DestMetaBadge>
                      <DestMetaBadge>
                        <DollarSign
                          size={11}
                          style={{
                            display: "inline",
                            marginRight: 2,
                            verticalAlign: "middle",
                          }}
                        />
                        {dest.averageCost}
                      </DestMetaBadge>
                    </DestMeta>
                  </DestContent>
                </DestCard>
              </MDiv>
            ))}
          </MDiv>
        </DestinationsGrid>
      </SectionWrapper>

      {/* ──────── TRUST BANNER ──────── */}
      <FadeUpSection>
        <TrustBanner>
          <TrustBannerInner>
            <TrustBannerText>
              <TrustBannerTitle>Votre AVI certifiée en 48h</TrustBannerTitle>
              <TrustBannerTitle>
                Garantie acceptée par tous les consulats.
              </TrustBannerTitle>
              <TrustBannerSub>
                Plus de 250 étudiants nous ont fait confiance. Rejoignez-les dès
                aujourd'hui.
              </TrustBannerSub>
            </TrustBannerText>
            <TrustBannerCTA to="/contact">
              Démarrer maintenant <ArrowRight size={18} />
            </TrustBannerCTA>
          </TrustBannerInner>
        </TrustBanner>
      </FadeUpSection>

      {/* ──────── STATS + WHY US ──────── */}
      <FadeUpSection>
        <MainFeature2
          subheading={<Subheading>Pourquoi nous choisir ?</Subheading>}
          heading={
            <>
              Des résultats qui{" "}
              <HighlightedText>parlent d'eux-mêmes.</HighlightedText>
            </>
          }
          description="Notre équipe accompagne les étudiants africains avec rigueur, transparence et réactivité. Chaque dossier est traité personnellement, chaque étudiant est suivi jusqu'à sa réussite."
          primaryButtonText="Commencer ma démarche"
          primaryButtonUrl="/contact"
          statistics={[
            { key: "Étudiants accompagnés", value: "250+" },
            { key: "Pays desservis", value: "4" },
            { key: "Taux de visa obtenu", value: "94%" },
            { key: "Années d'expérience", value: "5+" },
          ]}
          imageSrc={IMAGES.studentsImg}
          imageInsideDiv={true}
          imageCss={tw`shadow-xl`}
          imageDecoratorBlob={false}
          textOnLeft={true}
        />
      </FadeUpSection>

      {/* ──────── COMMENT ÇA MARCHE ──────── */}
      <StepsSection>
        <StepsWrapper>
          <FadeUpSection>
            <SectionTag tw="text-gray-500">Processus simple</SectionTag>
            <SectionTitle tw="text-white">
              Démarrez en <HighlightedText>4 étapes.</HighlightedText>
            </SectionTitle>
          </FadeUpSection>
          <StepsGrid>
            <MDiv
              style={{ display: "contents" }}
              variants={staggerContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {[
                {
                  n: "01",
                  title: "Choisissez un service",
                  desc: "Sélectionnez le service qui correspond à votre besoin : AVI, visa, logement, billets...",
                },
                {
                  n: "02",
                  title: "Soumettez vos documents",
                  desc: "Téléversez vos documents depuis votre espace étudiant sécurisé en quelques clics.",
                },
                {
                  n: "03",
                  title: "Suivez l'avancement",
                  desc: "Un conseiller dédié traite votre dossier et vous informe à chaque étape en temps réel.",
                },
                {
                  n: "04",
                  title: "Partez étudier !",
                  desc: "Votre AVI est prête, votre visa accordé, votre logement réservé. Il ne reste plus qu'à décoller !",
                },
              ].map((step) => (
                <MDiv
                  key={step.n}
                  variants={staggerItemVariants}
                  style={{ display: "contents" }}
                >
                  <StepItem>
                    <StepNumber>{step.n}</StepNumber>
                    <StepTitle>{step.title}</StepTitle>
                    <StepDesc>{step.desc}</StepDesc>
                  </StepItem>
                </MDiv>
              ))}
            </MDiv>
          </StepsGrid>
        </StepsWrapper>
      </StepsSection>

      {/* ──────── TÉMOIGNAGES ──────── */}
      <FadeUpSection>
        <Testimonial
          subheading={<Subheading>Témoignages</Subheading>}
          heading={
            <>
              Ils ont réalisé <HighlightedText>leur rêve.</HighlightedText>
            </>
          }
          description="Découvrez ce que nos étudiants disent de leur expérience avec MomsEduc Study. De l'obtention de leur AVI à leur installation, ils partagent leur parcours et les résultats obtenus grâce à notre accompagnement."
          testimonials={[
            {
              imageSrc: IMAGES.accueilImg,
              profileImageSrc:
                "https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=512&h=512&fit=facearea&facepad=2.25&q=80",
              quote:
                "Grâce à MomsEduc Study, j'ai obtenu mon AVI en 48h et mon visa en 3 semaines. Un accompagnement exceptionnel du début à la fin !",
              customerName: "WAPPI Kamel",
              customerTitle: "Étudiant — Cameroun → Paris",
            },
            {
              imageSrc:
                "https://images.unsplash.com/photo-1543269865-cbf427effbad?w=1024&q=80",
              profileImageSrc:
                "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=512&h=512&fit=facearea&facepad=3.25&q=80",
              quote:
                "Le service logement est incroyable. J'avais mon studio à Bruxelles avant même d'obtenir mon visa. Je recommande les yeux fermés !",
              customerName: "Aminata Diallo",
              customerTitle: "Étudiante — Dakar → Bruxelles",
            },
          ]}
          textOnLeft={false}
        />
      </FadeUpSection>

      {/* ──────── CTA FINAL ──────── */}
      <CtaFinal />
    </LandingLayout>
  );
};

export default HomePage;
